import React, { FC, lazy, useEffect, useRef, useState, Suspense, useCallback } from 'react';
import './theme.sass';
import Footer from '../footer/footer';
import { MessageService } from '../../../../message-service';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectEmbedUrl,
  selectdidPaginate,
  selectIsEmbedVisible,
  IUpdateMessagesPayload,
  updateMessages,
} from '../../modules/messages';
import { ITheme } from '../../app';
import { IProductsData, nextLinksSelector } from '../../modules/posts';
import { getConfig } from '../../../../url-reader';
import { ratingAggregationsSelector } from '../../modules/aggregations';
import { IPost } from '../../../../shared/types/Post';

import { AddPostButton, IAddPostButtonProps } from '../themes/common/add-post/add-post-button';
import { globalConfig } from '../../../../shared/config/config';
import NavList from '../nav-list/nav-list';
import NavSearch from '../nav-search/nav-search';
import NoPostCard from '../no-post-card/no-post-card';
import Nav, { INavConfig } from '../nav/nav';
import { TintAnalyticsTracker } from '../../../../shared/utils/tracker';
import { Nullable } from '../../../../shared/types/Typescript-utils';
import { isActivationKey } from '../../../../shared/utils/is-activation-key';
import { IAddReviewConfig } from '../themes/table/table';
import { ThemeNames } from './theme.utils';
import { useThemeConfig } from '../../hooks/use-theme-config';
import { usePopup } from '../../hooks/use-popup';

const LazyLoadSquarePhotoLayout = lazy(() => import('../themes/square-photo/square-photo-layout'));
const LazyLoadTallPhotoLayout = lazy(() => import('../themes/tall-photo/tall-photo-layout'));
const LazyLoadNewsLayout = lazy(() => import('../themes/news/news-layout'));
const LazyLoadHorizontalSliderLayout = lazy(() => import('../themes/horizontal-slider/horizontal-slider-layout'));
const LazyLoadVideoSlider = lazy(() => import('../themes/video-slider/video-slider'));
const LazyLoadGemLayout = lazy(() => import('../themes/gem/gem-layout'));
const LazyLoadGridTemplate = lazy(() => import('../themes/email-templates/grid/grid-template'));
const LazyLoadCollageTemplate = lazy(() => import('../themes/email-templates/collage/collage-template'));
const LazyLoadClassicLayout = lazy(() => import('../themes/classic/classic-layout'));
const LazyLoadTableLayout = lazy(() => import('../themes/table/table'));
const LazyLoadAtmosphereLayout = lazy(() => import('../themes/atmosphere/atmosphere'));

export interface StyleConfig {
  style: {
    [key: string]: string;
  };
}

interface IThemeProps {
  theme: ITheme;
  posts: readonly IPost[];
  products: IProductsData;
  navConfig: INavConfig;
  addPostConfig: IAddPostButtonProps;
  addReviewConfig: IAddReviewConfig;
  fetchNextPage: (isPagination: boolean) => void;
  banner: Nullable<{
    url: string;
    height: number;
  }>;
}

const isHex = (hex: Nullable<string>) => hex && /^#[0-9A-F]{6}$/i.test(hex);
const isRGBA = (rgba: Nullable<string>) => rgba && /^rgba\(((,?\s*\d+){3}).+$/.test(rgba);

const expendableTheme = [
  ThemeNames.NEW_CLASSIC,
  ThemeNames.NEW_ATMOSPHERE,
  ThemeNames.NEW_SLIDER,
  ThemeNames.VIDEO_SLIDER,
  ThemeNames.NEW_SQUARE_PHOTO,
  ThemeNames.NEW_TALL_PHOTO,
  ThemeNames.NEW_NEWS,
  ThemeNames.TABLE,
  ThemeNames.EMAIL_TEMPLATE_COLLAGE,
  ThemeNames.EMAIL_TEMPLATE_GRID,
];

const Theme: FC<IThemeProps> = ({
  children,
  theme,
  posts,
  products,
  navConfig,
  addPostConfig,
  addReviewConfig,
  fetchNextPage,
  banner,
}) => {
  const { personalization, meta, config, socialFeeds } = theme;
  const dispatch = useDispatch();
  const themeRef = useRef<HTMLDivElement>(null);
  const urlParams = getConfig();
  const embedUrl = useSelector(selectEmbedUrl);
  const didPaginate = useSelector(selectdidPaginate);
  const isEmbedVisible = useSelector(selectIsEmbedVisible);
  const ratingAggregations = useSelector(ratingAggregationsSelector);
  const nextLinksList = useSelector(nextLinksSelector);
  const [active, setActive] = useState(document.activeElement as HTMLInputElement);
  const tintAnalyticsTracker = TintAnalyticsTracker(!config.noTrack);
  const hasPublicPostingSocialFeed = Object.keys(socialFeeds).includes('public_post');
  const setMessages = (payload: IUpdateMessagesPayload) => dispatch(updateMessages(payload));

  const themeConfig = useThemeConfig({
    theme: personalization.attributes.theme,
    personalization,
    config,
  });

  const { openPopup, isPopupClosed, isPopupLoaded } = usePopup({
    theme,
    products,
    posts,
    addPostConfig,
    addReviewConfig,
    tintAnalyticsTracker,
  });

  const onNewContent = useCallback(
    (offsetHeightValue = themeRef?.current?.offsetHeight) => {
      // If someone went to hide the container and set the height for 0px
      if (urlParams.hideNoContent && themeRef.current && posts.length === 0) {
        MessageService.send({
          type: globalConfig.postMessage.actions.expandPostMessage,
          containerSize: 0,
        });
      } else if (themeRef.current) {
        MessageService.send({
          type: globalConfig.postMessage.actions.expandPostMessage,
          containerSize: offsetHeightValue + (banner?.height ? banner.height : 0),
        });
      }
    },
    [banner?.height, posts.length, urlParams.hideNoContent]
  );

  useEffect(() => {
    if (
      urlParams.expand &&
      !urlParams.outerFrame &&
      expendableTheme.includes(personalization.attributes.theme as any)
    ) {
      const body = document.querySelector('body');
      if (body !== null) {
        body.style.overflow = 'hidden';
      }

      // Check is the container size  ok every 2 seconds
      const interval = setInterval(() => {
        onNewContent();
      }, 2000);

      return () => {
        clearInterval(interval);
        if (body !== null) {
          body.style.overflow = 'auto';
        }
      };
    }
  }, [onNewContent, personalization.attributes.theme, urlParams.expand, urlParams.outerFrame]);

  useEffect(() => {
    if (
      urlParams.expand &&
      !urlParams.outerFrame &&
      expendableTheme.includes(personalization.attributes.theme as any) &&
      themeRef?.current?.offsetHeight &&
      themeRef.current.offsetHeight > 0
    ) {
      onNewContent(themeRef?.current?.offsetHeight);
    }
  }, [onNewContent, personalization.attributes.theme, urlParams.expand, urlParams.outerFrame]);

  useEffect(() => {
    if (didPaginate) {
      tintAnalyticsTracker.track('embed_paginated', {
        tint_id: meta.id,
      });
    }
  }, [didPaginate]);

  useEffect(() => {
    if (themeConfig?.waterfallConfig?.waterfallToggle && themeConfig?.waterfallConfig.highlightPostsToggle) {
      // Delay because of the problem in the old script
      setTimeout(() => {
        openPopup(posts[0]);
      }, 3000);
    }
  }, [isPopupLoaded]);

  useEffect(() => {
    const isMobile = globalConfig.regExp.mobile.test(navigator.userAgent);
    if (embedUrl != undefined) {
      tintAnalyticsTracker?.recordTraits({
        embed_url: embedUrl,
        device_category: isMobile ? 'mobile' : 'desktop',
        keen_export: 'true',
      });
    }
    if (personalization) {
      tintAnalyticsTracker.recordTraits({ personalization_id: personalization.id, keen_export: 'true' });
    }
    if (embedUrl && personalization) {
      tintAnalyticsTracker.track('embed_loaded', { tint_id: meta.id, keen_export: 'true' });
    }
  }, [embedUrl, personalization]);

  useEffect(() => {
    if (isEmbedVisible) {
      tintAnalyticsTracker.track('embed_viewed', {
        tint_id: meta.id,
      });
    }
  }, [isEmbedVisible]);

  const ctaClickHandler = (post: IPost): void => {
    tintAnalyticsTracker.track('embed_post_cta_clicked', {
      post_id: post.id,
      media_type: post.attributes.media_type,
    });
  };

  const authorClickHandler = (post: IPost): void => {
    tintAnalyticsTracker.track('embed_post_author_clicked', {
      post_id: post.id,
      media_type: post.attributes.media_type,
    });
  };

  const onSliderNext = () => {
    tintAnalyticsTracker.track('embed_paginated', {
      tint_id: meta.id,
    });
  };

  const onSliderPrev = () => {
    tintAnalyticsTracker.track('embed_paginated', {
      tint_id: meta.id,
    });
  };

  const handleEnterKey = (event: React.KeyboardEvent<HTMLDivElement>, post: IPost) => {
    // keys pressed on a CTA, link or button inside the post belong to that control
    if (event.target !== event.currentTarget || !isActivationKey(event)) {
      return;
    }
    // stop Space from scrolling the page
    event.preventDefault();
    setActive(document.activeElement as HTMLInputElement);
    openPopup(post);
  };

  useEffect(() => {
    if (
      isPopupClosed &&
      (active.classList.contains('classic-post') ||
        active.classList.contains('slide-container') ||
        active.classList.contains('square-photo-post'))
    ) {
      active.focus();
      setMessages({ key: 'isPopupClosed', value: false });
    }
  }, [isPopupClosed]);

  const defineBackground = (backgroundPageImage: Nullable<string>): string => {
    const urlMatcher = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
    if (
      backgroundPageImage &&
      (isHex(backgroundPageImage) || isRGBA(backgroundPageImage) || backgroundPageImage === 'transparent')
    ) {
      return backgroundPageImage;
    } else if (backgroundPageImage && backgroundPageImage.match(urlMatcher)) {
      return `url(${backgroundPageImage})`;
    } else {
      return '#ffffff';
    }
  };

  const background = defineBackground(themeConfig?.backgroundPageImage);

  const hasNextPage = !nextLinksList.includes(undefined);

  const renderAddPost =
    hasPublicPostingSocialFeed && !themeConfig?.waterfallConfig?.waterfallToggle
      ? props => <AddPostButton {...props} />
      : undefined;

  const isVisibleAddReviewButton = Object.keys(socialFeeds).includes('reviews');

  const renderNavList =
    Object.keys(socialFeeds).length !== 0 && !themeConfig?.waterfallConfig?.waterfallToggle
      ? props => <NavList {...props} />
      : undefined;

  const renderNavSearch =
    Object.keys(socialFeeds).length !== 0 && !themeConfig?.waterfallConfig?.waterfallToggle
      ? props => <NavSearch {...props} />
      : undefined;

  const renderTheme = (theme: JSX.Element) => (posts.length ? theme : <NoPostCard />);

  useEffect(() => {
    // If someone went to hide the container and set the height for 0px
    if (urlParams.hideNoContent && themeRef.current && posts.length === 0) {
      MessageService.send({
        type: globalConfig.postMessage.actions.expandPostMessage,
        containerSize: 0,
      });
    }
  }, [posts.length, urlParams.hideNoContent]);

  const renderLayout = () => {
    switch (personalization.attributes.theme) {
      case ThemeNames.TABLE:
        return (
          <>
            <LazyLoadTableLayout
              tintSlug={theme.config.tintSlug}
              posts={posts}
              ratingAggregations={ratingAggregations}
              addReviewConfig={addReviewConfig}
              navConfig={navConfig}
              fetchNextPage={fetchNextPage}
              openPopup={openPopup}
              postsConfig={themeConfig as any}
              isVisibleAddReviewButton={isVisibleAddReviewButton}
              hasNextPage={hasNextPage}
              personalization={theme.personalization.attributes.data}
            />
            <Footer theme={theme} onNewContent={onNewContent} />
          </>
        );

      case ThemeNames.NEW_ATMOSPHERE:
        return renderTheme(<LazyLoadAtmosphereLayout posts={posts} config={themeConfig as any} />);

      case ThemeNames.NEW_SQUARE_PHOTO:
        return (
          <>
            <Nav
              addPostConfig={addPostConfig}
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              renderAddPost={renderAddPost}
              renderNavSearch={renderNavSearch}
              renderNavList={renderNavList}
            />
            {renderTheme(
              <>
                <LazyLoadSquarePhotoLayout
                  posts={posts}
                  postsConfig={themeConfig as any}
                  openPopup={openPopup}
                  openPostOnEnter={handleEnterKey}
                  onCtaClick={ctaClickHandler}
                  isAutoScroll={
                    (themeConfig?.waterfallConfig?.waterfallToggle
                      ? themeConfig?.waterfallConfig?.waterfallToggle
                      : undefined) as any
                  }
                  transitionSpeed={themeConfig?.waterfallConfig?.waterfallSpeed as any}
                />
                <Footer theme={theme} onNewContent={onNewContent} />
              </>
            )}
          </>
        );

      case ThemeNames.NEW_TALL_PHOTO:
        return (
          <>
            <Nav
              addPostConfig={addPostConfig}
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              renderAddPost={renderAddPost}
              renderNavSearch={renderNavSearch}
              renderNavList={renderNavList}
            />
            {renderTheme(
              <>
                <LazyLoadTallPhotoLayout
                  posts={posts}
                  postsConfig={themeConfig as any}
                  openPopup={openPopup}
                  openPostOnEnter={handleEnterKey}
                  onCtaClick={ctaClickHandler}
                  isAutoScroll={
                    themeConfig?.waterfallConfig?.waterfallToggle
                      ? themeConfig?.waterfallConfig?.waterfallToggle
                      : undefined
                  }
                  transitionSpeed={themeConfig?.waterfallConfig?.waterfallSpeed}
                />
                <Footer theme={theme} />
              </>
            )}
          </>
        );

      case ThemeNames.NEW_NEWS:
        return (
          <>
            <Nav
              addPostConfig={addPostConfig}
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              renderAddPost={renderAddPost}
              renderNavSearch={renderNavSearch}
              renderNavList={renderNavList}
            />
            {renderTheme(
              <>
                <LazyLoadNewsLayout
                  isAutoScroll={
                    themeConfig?.waterfallConfig?.waterfallToggle
                      ? themeConfig?.waterfallConfig?.waterfallToggle
                      : undefined
                  }
                  posts={posts}
                  postsConfig={themeConfig as any}
                  openPopup={openPopup}
                  openPostOnEnter={handleEnterKey}
                  onCtaClick={ctaClickHandler}
                  transitionSpeed={themeConfig?.waterfallConfig?.waterfallSpeed}
                />
                {!themeConfig?.waterfallConfig?.waterfallToggle && <Footer theme={theme} onNewContent={onNewContent} />}
              </>
            )}
          </>
        );

      case ThemeNames.NEW_SLIDER:
        return (
          <>
            <Nav
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              addPostConfig={addPostConfig}
              renderAddPost={renderAddPost}
            />
            {renderTheme(
              <LazyLoadHorizontalSliderLayout
                posts={posts}
                fetchNextPage={fetchNextPage}
                config={themeConfig as any}
                openPopup={openPopup}
                onCtaClick={ctaClickHandler}
                openPostOnEnter={handleEnterKey}
                variant={themeConfig?.sliderIsCarousel}
                isAutoScroll={themeConfig?.autoScroll ? themeConfig.autoScroll : undefined}
                bannerHeight={navConfig.bannerHeight}
                onNext={onSliderNext}
                onPrev={onSliderPrev}
                sliderType={themeConfig?.sliderType}
              />
            )}
          </>
        );

      case ThemeNames.NEW_GEM:
        return renderTheme(
          <>
            <LazyLoadGemLayout
              posts={posts}
              shape={themeConfig?.shape}
              colors={themeConfig?.colors}
              direction={themeConfig?.direction}
              postPadding={themeConfig?.gemPadding}
              postWidth={themeConfig?.gemWidth}
              highlightDuration={themeConfig?.gemHighlightDuration}
              fontFamily={themeConfig?.fontFamily}
              gemAmount={themeConfig?.gemAmount}
              bannerHeight={navConfig.bannerHeight}
            />
          </>
        );
      case ThemeNames.EMAIL_TEMPLATE_GRID:
        return renderTheme(
          <LazyLoadGridTemplate
            posts={posts}
            postsConfig={themeConfig as any}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            draftPersonalization={urlParams?.draftPersonalization}
          />
        );
      case ThemeNames.EMAIL_TEMPLATE_COLLAGE:
        return renderTheme(
          <LazyLoadCollageTemplate
            posts={posts}
            postsConfig={themeConfig as any}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            draftPersonalization={urlParams?.draftPersonalization}
          />
        );

      case ThemeNames.VIDEO_SLIDER:
        return (
          <>
            <Nav
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              addPostConfig={addPostConfig}
              renderAddPost={renderAddPost}
            />
            {renderTheme(
              <LazyLoadVideoSlider
                posts={posts}
                fetchNextPage={fetchNextPage}
                config={themeConfig as any}
                openPopup={openPopup}
                onCtaClick={ctaClickHandler}
                openPostOnEnter={handleEnterKey}
                variant={themeConfig?.sliderIsCarousel}
                isAutoScroll={themeConfig?.autoScroll ? themeConfig.autoScroll : undefined}
                bannerHeight={navConfig.bannerHeight}
                onNext={onSliderNext}
                onPrev={onSliderPrev}
                sliderType={themeConfig?.sliderType}
              />
            )}
          </>
        );

      default:
        return (
          <>
            <Nav
              addPostConfig={addPostConfig}
              socialFeeds={socialFeeds}
              navConfig={navConfig}
              renderAddPost={renderAddPost}
              renderNavSearch={renderNavSearch}
              renderNavList={renderNavList}
            />
            {renderTheme(
              <>
                <LazyLoadClassicLayout
                  isAutoScroll={
                    themeConfig?.waterfallConfig?.waterfallToggle
                      ? themeConfig.waterfallConfig.waterfallToggle
                      : undefined
                  }
                  posts={posts}
                  postsConfig={themeConfig as any}
                  openPopup={openPopup}
                  openPostOnEnter={handleEnterKey}
                  onCtaClick={ctaClickHandler}
                  onAuthorClick={authorClickHandler}
                  transitionSpeed={themeConfig?.waterfallConfig?.waterfallSpeed}
                  draftPersonalization={urlParams?.draftPersonalization}
                />
                {!themeConfig?.waterfallConfig?.waterfallToggle && <Footer theme={theme} onNewContent={onNewContent} />}
              </>
            )}
          </>
        );
    }
  };

  const getThemeClassName = () => {
    switch (personalization.attributes.theme) {
      case ThemeNames.NEW_SLIDER:
        if (theme.config.expand) {
          return 'theme__height-max';
        } else {
          return '';
        }
      case ThemeNames.NEW_GEM:
        return 'theme__height-max';

      case ThemeNames.EMAIL_TEMPLATE_COLLAGE:
        return 'theme__collage';

      default:
        return '';
    }
  };

  return (
    <div
      className={`theme theme--${personalization.attributes.theme} ${getThemeClassName()}`}
      style={{
        background,
      }}
      ref={themeRef}
      data-testid="theme"
    >
      {children}
      <Suspense fallback={null}>{renderLayout()}</Suspense>
    </div>
  );
};

export default Theme;
